import { useCallback } from 'react'
import { ScrollView, View } from 'react-native'

import { ContentSectionData } from './ContentSectionsWithNav'

export const useScrollHandlers = ({
  sectionsRefs,
  // eslint-disable-next-line unused-imports/no-unused-vars
  scrollViewRef,
  linksRefs,
  linksContainerRef,
  linksContainerHeight,
  sections,
  isEnabled,
  setActiveSectionId,
  onSectionChange,
  linksScrollOffset = 0,
}: {
  sectionsRefs: React.MutableRefObject<Map<string, View | null>>
  scrollViewRef?: React.RefObject<ScrollView>
  linksRefs: React.MutableRefObject<Map<string, View | null>>
  linksContainerRef: React.RefObject<ScrollView>
  linksContainerHeight: React.MutableRefObject<number>
  sections: ContentSectionData[]
  isEnabled: boolean
  setActiveSectionId: React.Dispatch<React.SetStateAction<string | null>>
  onSectionChange?: (sectionId: string) => void
  linksScrollOffset?: number
}) => {
  return {
    scrollToSection: useCallback(
      (sectionId: string) => {
        const element = sectionsRefs.current?.get(sectionId) as unknown as HTMLElement
        if (element) {
          const sectionOffset = element.getBoundingClientRect().top
          const scrollY = window.pageYOffset
          const totalOffset = sectionOffset + scrollY - linksContainerHeight.current
          window.scrollTo({
            top: totalOffset,
            behavior: 'smooth',
          })
        }
      },
      [sectionsRefs, linksContainerHeight],
    ),
    scrollToLinkButton: useCallback(
      (sectionId: string) => {
        const element = linksRefs.current?.get(sectionId) as unknown as HTMLElement
        const container = linksContainerRef.current as unknown as HTMLElement
        if (element && container) {
          const elementRect = element.getBoundingClientRect()
          const containerRect = container.getBoundingClientRect()
          const scrollLeft = container.scrollLeft + (elementRect.left - containerRect.left) - linksScrollOffset
          container.scrollTo(0, scrollLeft)
          return
        }
      },
      [linksContainerRef, linksRefs, linksScrollOffset],
    ),
    handleScroll: useCallback(() => {
      if (!isEnabled) return

      const headerOffset = linksContainerHeight.current
      let topMostVisibleSection = null

      for (const section of sections) {
        const element = sectionsRefs.current?.get(section.id)
        if (element) {
          const htmlElement = element as unknown as HTMLElement
          const rect = htmlElement.getBoundingClientRect()

          // Section intersects with header
          if (rect.top <= headerOffset && rect.bottom > headerOffset) {
            topMostVisibleSection = section.id
            break
          }
          // Section is below header
          else if (rect.top >= headerOffset) {
            topMostVisibleSection = section.id
            break
          }
        }
      }

      const sectionToActivate = topMostVisibleSection || sections[0]?.id
      setActiveSectionId(sectionToActivate)
      onSectionChange?.(sectionToActivate)
    }, [isEnabled, linksContainerHeight, sections, setActiveSectionId, onSectionChange, sectionsRefs]),
  }
}
